import Controller from '@ember/controller';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type MarketExecutiveSpiderDashboardRoute from 'lumen/routes/market/executive/spider-dashboard';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { ModelFrom } from 'lumen/utils/routes';

export default class MarketExecutiveSpiderDashboardController extends Controller {
  declare model: ModelFrom<MarketExecutiveSpiderDashboardRoute>;

  queryParams = [
    'brandIds',
    'brandParentIds',
    'cities',
    'classes',
    'data_replatforming',
    'debug',
    'dateRangeEnd',
    'dateRangeStart',
    'dormIds',
    'forecastAsof',
    'heatmapFilter',
    'heatmapMode',
    'heatmapTab',
    'heatmapYear',
    'hotelIds',
    'left_asofDate',
    'left_bookingPeriodEnd',
    'left_bookingPeriodStart',
    'left_leadTime',
    'left_leadTimeComp',
    'left_lengthOfStay',
    'left_lengthOfStayComp',
    'left_marketSegments',
    'left_roomTypeCategory',
    'left_roomTypes',
    'left_statuses',
    'left_stayPeriodEnd',
    'left_stayPeriodStart',
    'left_weekdays',
    'otaiDormIds',
    'otaiRegionalDosIds',
    'otaiRegionalVpIds',
    'ownerships',
    'pickupFrom',
    'portfolioIds',
    'portfolioRegions',
    'regionalDosIds',
    'regionalVpIds',
    'regions',
    'right_asofDate',
    'right_bookingPeriodEnd',
    'right_bookingPeriodStart',
    'right_leadTime',
    'right_leadTimeComp',
    'right_lengthOfStay',
    'right_lengthOfStayComp',
    'right_marketSegments',
    'right_roomTypeCategory',
    'right_roomTypes',
    'right_statuses',
    'right_stayPeriodEnd',
    'right_stayPeriodStart',
    'right_weekdays',
    'view',
  ];

  @service declare currency: Services['currency'];
  @service declare currencyConversion: Services['currency-conversion'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;

  @tracked brandIds: string | null = null;
  @tracked brandParentIds: string | null = null;
  @tracked dormIds: string | null = null;
  @tracked hotelIds: string | null = null;
  @tracked otaiDormIds: string | null = null;
  @tracked otaiRegionalDosIds: string | null = null;
  @tracked otaiRegionalVpIds: string | null = null;
  @tracked portfolioIds: string | null = null;
  @tracked regionalDosIds: string | null = null;
  @tracked regionalVpIds: string | null = null;

  declare dashboard: string;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get extendedQueryParams() {
    if (!this.hotelAndMarketService.isMyHotels) {
      return this.iframeQPs;
    }

    return {
      marketId: this.model.id,
      ...this.iframeQPs,
    };
  }

  get iframeQPs(): Record<string, unknown> {
    const iframeQPs: Record<string, unknown> = {
      iframe: 1,
      origin: window.location.origin,
    };

    if (this.currency.selectedCurrency) {
      iframeQPs['currency'] = this.currency.selectedCurrency;
    }

    if (this.currencyConversion.selectedProfileId) {
      iframeQPs['currencyVariant'] = this.currencyConversion.selectedProfileId;
    }

    if (this.hotelAndMarketService.hotel) {
      iframeQPs['subscriptionId'] = this.hotelAndMarketService.hotel.id;
    }

    this.queryParams.forEach((queryParam) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const value = this[queryParam]!;

      if (value) {
        iframeQPs[queryParam] = value;
      }
    });

    return iframeQPs;
  }

  get urlForEmbeddedApp(): string {
    const { dashboard, model } = this;

    if (this.hotelAndMarketService.isMyHotels) {
      return `/dashboards/executive/${dashboard}`;
    }

    return `/dashboards/portfolio-snapshot/${model.id}/${dashboard}`;
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    this.queryParams.forEach((queryParam) => {
      // @ts-expect-error: Incorrect type
      this[queryParam] = null;
    });
  }
}
